import React, { Suspense } from 'react';
import { Navigate, Outlet, Route } from 'react-router';
import { Icons } from '../../../../config';
import { createDocumentRoute, createIntegrationRoute } from '../route';
import { ResearchShellConfig, ResesarchShellInit } from './researchShellConfig';
import { createReportingRootContainer } from './page/root/reportingRootContainer';
import { createReportingRootController } from './page/root/reportingRootController';
import { createReportingRootLoader } from './page/root/reportingRootLoader';
import {
    ReportingShellContainerBaseConfig,
    ReportingShellControllerBaseConfig,
    ReportingShellLoaderBaseConfig,
    ReportingShellPubsub,
    ReportingShellStrategy,
    ReportingShellViewBaseConfig,
} from './base';
import {
    createNotificationListController,
    createReportingAccountController,
    createReportingNavigationController,
    createShortcutListController,
    createTaskListController,
    createThreadListContainer,
    createThreadListLoader,
    createThreadListController,
    createThreadListView,
} from './view';
import { ResearchShellComponents, ResearchShellEnhancer } from './researchShellInterface';
import {
    ReportingShellContext,
    createReportingContextController,
    createReportingContextLoader,
    createReportingErrorContainer,
    createReportingErrorController,
    createReportingShellContextContainer,
    createReportingShellContextProvider,
    createChatOverlayContainer,
    createChatOverlayController,
    createChatOverlayLoader,
    createChatOverlayView,
    createChatDetailContainer,
    createChatDetailController,
    createChatDetailView,
    createChatDetailLoader,
    createChatApplyController,
    createChatApplyContainer,
    createChatApplyLoader,
    createChatItemCreateContainer,
    createChatItemCreateLoader,
    createChatItemCreateController,
    createChatDetailContext,
    createChatComposerContainer,
    createChatComposerController,
    createChatComposerLoader,
    createChatCreateContainer,
    createChatCreateController,
    createChatCreateLoader,
    createChatCreateView,
    createChatLayoutView,
    createChatComposerView,
    createChatItemSearchLoader,
    createChatItemSearchContainer,
    createChatItemSearchController,
    createChatItemReadContainer,
    createChatItemReadLoader,
    createChatItemReadController,
    createChatItemTaskController,
    createChatItemTaskLoader,
    createChatItemTaskContainer,
    createChatItemAssistantTaskContainer,
    createChatItemReplaceContainer,
    createChatItemReplaceLoader,
    createChatItemReplaceController,
    createChatErrorContainer,
    createChatErrorController,
    createChatErrorLoader,
    createInsertOverlayContainer,
    createInsertOverlayLoader,
    createInsertOverlayController,
    createInsertOverlayView,
    createInsertOverviewController,
    createInsertOverviewContainer,
    createInsertOverviewLoader,
    createInsertOverviewView,
} from './page';
import { Center, Grid, GridItem, HStack, VStack } from '@chakra-ui/react';
import { createEventProvider } from '@varos/client-base';
import { CardSpinner } from '../../../../domain';
import { useNavigate } from 'react-router-dom';
import {
    createReportingSidebarContainer,
    createReportingSidebarLoader,
    createReportingSidebarController,
    createReportingSidebarView,
} from './page/sidebar';
import { createTreeItemContainer } from './view/tree/item';
import { createTreeItemController } from './view/tree/item';
import { createResearchShellStateProvider } from './researchShellState';
import { createChatLayoutLoader } from './page/chat/layout/chatLayoutLoader';
import { createChatLayoutContainer } from './page/chat/layout';
import { createChatLayoutController } from './page/chat/layout';
import { HiOutlineHome } from 'react-icons/hi';
import { GoWorkflow } from 'react-icons/go';
import { createCommonUI } from '../route/document/view';
import { createShellNavigatorController, createNavigatorService } from './strategy';
import {
    createTemplateDetailContainer,
    createTemplateDetailController,
    createTemplateDetailLoader,
    createTemplateDetailRoute,
    createTemplateDetailView,
} from './page/template/detail';
import {
    createThreadItemContainer,
    createThreadItemController,
} from './view/thread/item';
import { TypeOf } from 'zod';
import { ThreadNewUrlParamsSchema } from '../route/integration/page/thread/new';
import { createChatRoute } from '../route/chat';
import { createChatItemAssistantTaskController } from './page/chat/detail/assistant-task';
import { createChatItemAssistantTaskLoader } from './page/chat/detail/assistant-task';

export function createResearchShell(
    config: ResesarchShellInit,
    enhancer?: ResearchShellEnhancer
) {
    return create(
        {
            ...config,
            provider: {
                createRootController: createReportingRootController,
                createNavigationController: createReportingNavigationController,
                createAccountController: createReportingAccountController,
                createNotificationController: createNotificationListController,
                createShortcutController: createShortcutListController,
                createDocumentRoute: createDocumentRoute,
                createIntegrationRoute: createIntegrationRoute,
                createChatRoute: createChatRoute,
                createTaskListController: createTaskListController,
            },
            enhancer: {
                reporting: null,
            },
        },
        enhancer
    );
}

function create(
    config: ResearchShellConfig,
    enhancer?: ResearchShellEnhancer
): ResearchShellComponents {
    if (enhancer) {
        return enhancer(create)(config);
    }
    const Context = React.createContext<ReportingShellContext | null>(null);

    const loaderConfig: ReportingShellLoaderBaseConfig = {
        kernel: config.kernel,
        repository: config.repository,
        hook: config.hook,
    };

    const strategy: ReportingShellStrategy = {
        navigator: {
            controller: createShellNavigatorController(),
            service: createNavigatorService(),
        },
    };

    const controllerConfig: ReportingShellControllerBaseConfig = {
        kernel: config.kernel,
        view: config.view,
        hook: config.hook,
        strategy,
    };

    const router = {
        insert: config.kernel.provider.createRouter({
            state: {
                kind: 'memory',
                initialPath: '',
            },
        }),
    };

    const viewConfig: ReportingShellViewBaseConfig = {
        UI: config.UI,
        router: router,
    };

    const states = createResearchShellStateProvider();

    const contexts = createReportingShellContextProvider(
        createReportingShellContextContainer(
            config,
            Context,
            createReportingContextLoader(loaderConfig),
            createReportingContextController(controllerConfig)
        ),
        Context
    );
    const pubsub: ReportingShellPubsub = {
        router: createEventProvider(),
    };

    const containerConfig: ReportingShellContainerBaseConfig = {
        basePath: config.basePath,
        kernel: config.kernel,
        context: contexts,
        Theme: config.Theme,
        view: config.view,
        UI: config.UI,
        repository: config.repository,
        state: states,
        pubsub,
        hook: config.hook,
        strategy,
        router,
        external: {
            integration: {
                open(item, props) {
                    const options: TypeOf<typeof ThreadNewUrlParamsSchema> = {
                        tool: item.tool.id,
                        auto_submit: true,
                    };
                    const params = new URLSearchParams(
                        Object.fromEntries(
                            Object.entries(options)
                                .filter(([_, value]) => value !== null)
                                .map(([key, value]) => [key, value?.toString() ?? ''])
                        )
                    );
                    props.navigate(
                        `/reporting/integrations/${item.integration.id}?${params.toString()}`
                    );
                },
            },
        },
    };

    const loader = createReportingRootLoader(loaderConfig);
    const controller = config.provider.createRootController({
        ...controllerConfig,
        controller: {
            navigation: config.provider.createNavigationController(controllerConfig),
            account: config.provider.createAccountController(controllerConfig),
            notification: config.provider.createNotificationController(controllerConfig),
            task: config.provider.createTaskListController(controllerConfig),
        },
    });

    const ThreadListContainer = createThreadListContainer(
        containerConfig,
        createThreadListLoader(loaderConfig),
        createThreadListController(controllerConfig),
        createThreadListView({
            ...config,
            Container: {
                Thread: {
                    Item: createThreadItemContainer(
                        containerConfig,
                        createThreadItemController()
                    ),
                },
            },
        })
    );

    const SidebarContainer = createReportingSidebarContainer(
        {
            ...containerConfig,
            navigation: {
                items: [
                    {
                        id: 'home',
                        label: 'Home',
                        Icon: HiOutlineHome,
                        path: '/reporting/home',
                    },
                    {
                        id: 'tasks',
                        label: 'Tasks',
                        Icon: GoWorkflow,
                        path: '/reporting/tasks',
                    },
                ],
            },
        },
        createReportingSidebarLoader(loaderConfig),
        createReportingSidebarController(config)
    );

    const TreeItemContainer = createTreeItemContainer(
        containerConfig,
        createTreeItemController(controllerConfig)
    );

    const Shell = createReportingRootContainer(
        {
            ...containerConfig,
            navigation: {
                // items: navigationItems,
                items: [],
            },
            Container: {
                Error: createReportingErrorContainer(
                    containerConfig,
                    createReportingErrorController()
                ),
            },
        },
        loader,
        controller
    );

    const Layout = {
        Centered: config.layout.centered.create({}),
    };

    const {
        Pages: {
            Report: ReportPage,
            Content: ContentPage,
            Document: DocumentPage,
            Record: RecordPage,
            Snippet: SnippetPage,
            Task: TaskPage,
        },
    } = config.provider.createDocumentRoute(
        {
            UI: {
                ...config.UI,
                View: createCommonUI(),
            },
            Layout,
            kernel: config.kernel,
            context: contexts,
            controller: config.view,
            repository: config.repository,
            state: states,
            pubsub: pubsub.router,
        },
        config.enhancer.reporting ?? undefined
    );

    const chat = config.provider.createChatRoute({
        UI: {
            ...config.UI,
        },
        Layout,
        kernel: config.kernel,
        context: contexts,
        external: {
            document: {
                open(item, props) {
                    props.navigate(`/reporting/reports/${item.document.id}`);
                },
            },
        },
        controller: config.view,
        repository: config.repository,
        state: states,
        hook: config.hook,
    });

    const {
        Pages: { Integration: IntegrationPage },
    } = config.provider.createIntegrationRoute({
        UI: {
            ...config.UI,
        },
        Layout,
        kernel: config.kernel,
        context: contexts,
        external: {
            document: {
                open(item, props) {
                    props.navigate(`/reporting/reports/${item.document.id}`);
                },
            },
        },
        controller: config.view,
        repository: config.repository,
        state: states,
        hook: config.hook,
        route: {
            chat,
        },
    });

    const { Provider: ContextProvider } = contexts;

    const Sidebar = createReportingSidebarView({
        ...config,
        Container: {
            Tree: TreeItemContainer,
            ThreadList: ThreadListContainer,
        },
    });

    const context = {
        chat: {
            context: createChatDetailContext(),
        },
    };

    const Composer = createChatComposerContainer(
        {
            ...containerConfig,
            chat: context.chat,
        },
        createChatComposerLoader(loaderConfig),
        createChatComposerController(controllerConfig),
        createChatComposerView({
            ...viewConfig,
        })
    );

    const Insert = {
        Overlay: createInsertOverlayContainer(
            containerConfig,
            createInsertOverlayLoader(loaderConfig),
            createInsertOverlayController(controllerConfig),
            createInsertOverlayView({
                ...viewConfig,
                Container: {
                    Insert: createInsertOverviewContainer(
                        containerConfig,
                        createInsertOverviewLoader(loaderConfig),
                        createInsertOverviewController(controllerConfig),
                        createInsertOverviewView({
                            ...viewConfig,
                        })
                    ),
                },
                Route: {
                    Template: {
                        Detail: createTemplateDetailRoute(
                            containerConfig,
                            createTemplateDetailContainer(
                                {
                                    ...containerConfig,
                                },
                                createTemplateDetailLoader(loaderConfig),
                                createTemplateDetailController(controllerConfig),
                                createTemplateDetailView({
                                    ...viewConfig,
                                })
                            )
                        ),
                    },
                },
            })
        ),
    };

    const Chat = {
        Overlay: createChatOverlayContainer(
            containerConfig,
            createChatOverlayLoader(loaderConfig),
            createChatOverlayController(controllerConfig),
            createChatOverlayView({
                ...viewConfig,
                Container: {
                    Layout: createChatLayoutContainer(
                        {
                            ...containerConfig,
                            chat: context.chat,
                        },
                        createChatLayoutLoader(loaderConfig),
                        createChatLayoutController(controllerConfig),
                        createChatLayoutView({
                            ...viewConfig,
                            Container: {
                                Error: createChatErrorContainer(
                                    containerConfig,
                                    createChatErrorLoader(loaderConfig),
                                    createChatErrorController(controllerConfig)
                                ),
                            },
                        })
                    ),
                    Detail: createChatDetailContainer(
                        {
                            ...containerConfig,
                            chat: context.chat,
                        },
                        createChatDetailLoader(loaderConfig),
                        createChatDetailController(controllerConfig),
                        createChatDetailView({
                            ...viewConfig,
                            Container: {
                                Item: {
                                    Replace: createChatItemReplaceContainer(
                                        containerConfig,
                                        createChatItemReplaceLoader(loaderConfig),
                                        createChatItemReplaceController()
                                    ),
                                    Apply: createChatApplyContainer(
                                        containerConfig,
                                        createChatApplyLoader(loaderConfig),
                                        createChatApplyController()
                                    ),
                                    Create: createChatItemCreateContainer(
                                        containerConfig,
                                        createChatItemCreateLoader(loaderConfig),
                                        createChatItemCreateController()
                                    ),
                                    Search: createChatItemSearchContainer(
                                        containerConfig,
                                        createChatItemSearchLoader(loaderConfig),
                                        createChatItemSearchController()
                                    ),
                                    Read: createChatItemReadContainer(
                                        containerConfig,
                                        createChatItemReadLoader(loaderConfig),
                                        createChatItemReadController()
                                    ),
                                    Task: createChatItemTaskContainer(
                                        {
                                            ...containerConfig,
                                            navigation: {
                                                task(props) {
                                                    return {
                                                        path: `/${config.basePath}/tasks`,
                                                    };
                                                },
                                            },
                                        },
                                        createChatItemTaskLoader(loaderConfig),
                                        createChatItemTaskController()
                                    ),
                                    AssistantTask: createChatItemAssistantTaskContainer (
                                        containerConfig,
                                        createChatItemAssistantTaskLoader   (loaderConfig),
                                        createChatItemAssistantTaskController()
                                    ),
                                },
                                Composer,
                            },
                        })
                    ),
                    Create: createChatCreateContainer(
                        containerConfig,
                        createChatCreateLoader(loaderConfig),
                        createChatCreateController(controllerConfig),
                        createChatCreateView({
                            ...viewConfig,
                            Container: {
                                Composer,
                            },
                        })
                    ),
                },
            })
        ),
    };

    const LayoutView: React.FC<{ children: React.ReactNode }> = states.chat.subscribe(
        states.insert.subscribe((props) => {
            const context = contexts.useContext();
            const chatState = states.chat.useState(context);
            const insertState = states.insert.useState(context);
            const layoutState = states.layout.useState(context);

            const [isScrolling, setIsScrolling] = React.useState(false);
            const scrollTimeoutRef = React.useRef<NodeJS.Timeout>();

            const handleScroll = React.useCallback(() => {
                setIsScrolling(true);

                // Clear existing timeout
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                }

                // Set new timeout to hide scrollbar
                scrollTimeoutRef.current = setTimeout(() => {
                    setIsScrolling(false);
                }, 1000); // Hide after 1 second of no scrolling
            }, []);

            React.useEffect(() => {
                return () => {
                    if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                    }
                };
            }, []);

            // Get overlay widths from state, with fallback to default
            const chatWidth = layoutState.value.chatWidth || 384; // 24rem default
            const insertWidth = 400; // 25rem default for insert overlay

            // Handle mutual exclusivity - insert overlay takes precedence over chat overlay
            React.useEffect(() => {
                if (insertState.value.overlay.isOpen && chatState.value.isOpen) {
                    // If both are requested to be open, insert takes precedence and we close chat
                    chatState.onChange({ isOpen: false });
                }
            }, [
                insertState.value.overlay.isOpen,
                chatState.value.isOpen,
                chatState,
                insertState,
            ]);

            // Computed overlay visibility - only one overlay can be shown at a time
            const visibleOverlay = React.useMemo(() => {
                if (insertState.value.overlay.isOpen) {
                    return 'insert';
                } else if (chatState.value.isOpen) {
                    return 'chat';
                } else {
                    return null;
                }
            }, [insertState.value.overlay.isOpen, chatState.value.isOpen]);

            // Determine grid template columns based on visible overlay
            const templateColumns = React.useMemo(() => {
                switch (visibleOverlay) {
                    case 'insert':
                        return `1fr ${insertWidth}px`;
                    case 'chat':
                        return `1fr ${chatWidth}px`;
                    default:
                        return '1fr';
                }
            }, [visibleOverlay, insertWidth, chatWidth]);

            return (
                <>
                    <Grid
                        position="absolute"
                        top={0}
                        bottom={0}
                        left={0}
                        right={0}
                        templateColumns={templateColumns}
                    >
                        <GridItem
                            overflowY="auto"
                            pb={{ base: 24, xl: 32 }}
                            position="relative"
                            onScroll={handleScroll}
                            sx={{
                                scrollbarGutter: 'stable',
                                '&::-webkit-scrollbar': {
                                    '-webkit-appearance': 'none',
                                    width: '0.5rem',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-corner': {
                                    '-webkit-appearance': 'none',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    borderRadius: '1rem',
                                    background: 'rgba(255,255,255,0.3)',
                                    display: isScrolling ? 'block' : 'none',
                                },
                            }}
                        >
                            <Suspense
                                fallback={
                                    <Center h="full" w="full">
                                        <CardSpinner />
                                    </Center>
                                }
                            >
                                {props.children}
                            </Suspense>
                        </GridItem>
                        {insertState.value.overlay.isOpen && (
                            <GridItem>
                                <Suspense
                                    fallback={
                                        <Center h="full" w="full">
                                            <CardSpinner />
                                        </Center>
                                    }
                                >
                                    <Insert.Overlay />
                                </Suspense>
                            </GridItem>
                        )}
                        {chatState.value.isOpen && (
                            <GridItem>
                                <Suspense
                                    fallback={
                                        <Center h="full" w="full">
                                            <CardSpinner />
                                        </Center>
                                    }
                                >
                                    <Chat.Overlay
                                        initialWidth={{ base: '20rem', xl: '25dvw' }}
                                        minWidth={{ base: '12rem' }}
                                        maxWidth={{ base: '50dvw', xl: '33dvw' }}
                                    />
                                </Suspense>
                            </GridItem>
                        )}
                    </Grid>
                    <ContentPage.Overlay />
                    {/* <RecordPage.Detail /> */}
                    <RecordPage.Upsert />
                    <SnippetPage.Overlay />
                </>
            );
        })
    );

    const element = (
        <Route
            path={config.basePath}
            element={
                <ContextProvider>
                    <Shell>
                        <Grid
                            pos="absolute"
                            top={0}
                            bottom={0}
                            left={0}
                            right={0}
                            templateColumns={{
                                sm: '1fr',
                                md: 'min-content 1fr',
                            }}
                            gap={0}
                        >
                            <GridItem
                                overflowY="scroll"
                                display={{ sm: 'none', md: 'block' }}
                            >
                                <SidebarContainer
                                    as={Sidebar}
                                    initialWidth={{ base: '20rem', xl: '25dvw' }}
                                    minWidth={{ base: '12rem' }}
                                    maxWidth={{ base: '50dvw', xl: '33dvw' }}
                                />
                            </GridItem>
                            <GridItem position="relative">
                                <Outlet />
                            </GridItem>
                        </Grid>
                    </Shell>
                </ContextProvider>
            }
        >
            <Route index={true} element={<Navigate to="home" replace={true} />} />
            <Route
                path="home"
                element={
                    <LayoutView>
                        <ReportPage.Generate />
                    </LayoutView>
                }
            />
            <Route
                path="tasks"
                element={
                    <LayoutView>
                        <TaskPage.List />
                    </LayoutView>
                }
            />
            <Route
                path="reports"
                element={
                    <LayoutView>
                        <Outlet />
                    </LayoutView>
                }
            >
                <Route path="new" element={<DocumentPage.New />} />
                <Route path=":contentId" element={<DocumentPage.Detail />} />
            </Route>
            <Route
                path="integrations"
                element={
                    <LayoutView>
                        <Outlet />
                    </LayoutView>
                }
            >
                <Route path=":integrationId" element={<IntegrationPage.Detail />} />
            </Route>
        </Route>
    );

    return {
        element,
    };
}
